import React, { useEffect } from 'react';
import Header from './Header';
import './ShikumiSeibiLP.css';

const serviceUrl = 'https://shikumi-seibi.jp/';
const contactUrl = 'https://shikumi-seibi.jp/contact';

export default function ShikumiSeibiLP() {
  useEffect(() => {
    const previousTitle = document.title;
    const existingDescription = document.querySelector('meta[name="description"]');
    const description = existingDescription || document.createElement('meta');
    const previousDescription = description.getAttribute('content');
    if (!existingDescription) {
      description.setAttribute('name', 'description');
      document.head.appendChild(description);
    }
    document.title = 'つくったあと | 社内ツールの改修・運用支援';
    description.setAttribute('content', 'AIで作った社内ツールの改修と運用を支援します。利用権限・データ保存先・維持費・更新と引継ぎを整理。無料相談から始められます。');
    return () => {
      document.title = previousTitle;
      if (!existingDescription) description.remove();
      else if (previousDescription === null) description.removeAttribute('content');
      else description.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="home shikumi-page">
      <Header />
      <main className="shikumi-main">
        <section className="shikumi-hero" aria-labelledby="shikumi-title">
          <div className="shikumi-wrap shikumi-hero-grid">
            <div>
              <p className="shikumi-eyebrow">社内ツールの改修・運用支援</p>
              <p className="shikumi-brand">つくったあと<span>SHIKUMI SEIBI</span></p>
              <h1 id="shikumi-title">AIで作った社内ツールの<br className="shikumi-desktop-break" />改修と運用を支援します。</h1>
              <p className="shikumi-lead">動き始めたツールを、日々の業務で使い続けるために。<br />今あるツールを確認し、必要な改修と運用の整備をご提案します。</p>
              <div className="shikumi-actions">
                <a className="shikumi-button" href={contactUrl}>無料で相談する <span aria-hidden="true">↗</span></a>
                <a className="shikumi-text-link" href="#shikumi-pricing">料金を見る <span aria-hidden="true">↓</span></a>
              </div>
              <p className="shikumi-note">初回相談は無料。コードや仕様書の準備は不要です。</p>
            </div>
            <figure className="shikumi-diagram">
              <figcaption>ツールを使い続けるための整理<span>説明用のイメージ</span></figcaption>
              <div className="shikumi-diagram-center"><span aria-hidden="true">▦</span><strong>今ある社内ツール</strong><small>業務に合わせて、改修・整備</small></div>
              <div className="shikumi-diagram-items">
                <div><span>01 / 利用権限</span><strong>誰が使う？</strong></div>
                <div><span>02 / データ保存先</span><strong>どこに保存する？</strong></div>
                <div><span>03 / 維持費</span><strong>いくらかかる？</strong></div>
                <div><span>04 / 更新・引継ぎ</span><strong>どう続ける？</strong></div>
              </div>
              <p>ツールの構成と運用の手順を、確認できる形に。</p>
            </figure>
          </div>
        </section>

        <section className="shikumi-section" aria-labelledby="shikumi-concerns">
          <div className="shikumi-wrap shikumi-split">
            <div><p className="shikumi-eyebrow">こんなときに</p><h2 id="shikumi-concerns">使い始めてからの<br />困りごとをご相談ください。</h2></div>
            <ul className="shikumi-concerns">
              <li><h3>他の担当者にも使ってもらいたい</h3><p>共有方法や利用権限、担当が変わったときの引継ぎを整理したい。</p></li>
              <li><h3>AIで改善を続けたい</h3><p>変更した内容の確認方法や、業務で使う環境への公開手順を決めたい。</p></li>
              <li><h3>契約や請求が増えてきた</h3><p>何にいくらかかっているかを把握し、今後の維持費を見通したい。</p></li>
            </ul>
          </div>
        </section>

        <section className="shikumi-section shikumi-tinted" aria-labelledby="shikumi-support">
          <div className="shikumi-wrap">
            <p className="shikumi-eyebrow">支援内容</p><h2 id="shikumi-support">仕組みを確認し、直して、<br />次の担当者へ渡せる状態に。</h2>
            <p className="shikumi-section-lead">調査で分かったことをもとに、改修の範囲と優先順位を一緒に決めます。</p>
            <div className="shikumi-support-grid">
              <article><span className="shikumi-number">01</span><h3>構成と改修方針を整理</h3><p>ツールの構成、利用権限、データ保存先を確認し、必要な改修を整理します。</p><p className="shikumi-deliverable">お渡しするもの<strong>構成図と改修計画</strong></p></article>
              <article><span className="shikumi-number">02</span><h3>費用を見通す</h3><p>クラウドやAI APIなどの利用状況を確認し、続けるための費用を比較します。</p><p className="shikumi-deliverable">お渡しするもの<strong>12か月の費用比較</strong></p></article>
              <article><span className="shikumi-number">03</span><h3>運用の手順を残す</h3><p>更新の確認方法、問題が起きたときの復旧、担当者への引継ぎを整理します。</p><p className="shikumi-deliverable">お渡しするもの<strong>更新・復旧・引継ぎ資料</strong></p></article>
            </div>
          </div>
        </section>

        <section id="shikumi-pricing" className="shikumi-section" aria-labelledby="shikumi-price-title">
          <div className="shikumi-wrap">
            <p className="shikumi-eyebrow">料金</p><h2 id="shikumi-price-title">診断から、必要な支援を選べます。</h2>
            <p className="shikumi-section-lead">料金はすべて税込です。診断のみのご依頼も承ります。</p>
            <div className="shikumi-pricing">
              <article className="shikumi-price-row"><div><span className="shikumi-step-label">まずは現状を確認</span><h3>診断</h3></div><p className="shikumi-price">55,000<span>円 / 単発</span></p><div><p>コードや構成を調べ、改修の方針を整理します。</p><small>実装・移行は含みません。単独で依頼できます。</small></div></article>
              <article className="shikumi-price-row"><div><span className="shikumi-step-label">合意した範囲を改修</span><h3>初期整備</h3></div><p className="shikumi-price">220,000<span>円〜</span></p><div><p>診断をもとに、必要な改修と運用の整備を行います。</p><small>診断料55,000円が別途必要です。大規模な作り直し・移行は別途お見積りします。</small></div></article>
              <article className="shikumi-price-row"><div><span className="shikumi-step-label">ご希望に応じて</span><h3>継続運用</h3></div><p className="shikumi-price">33,000<span>円 / 月</span></p><div><p>点検・費用確認・報告・相談・小修正を、合わせて月3時間まで。</p><small>任意の契約です。超過する作業は事前にお見積りします。</small></div></article>
            </div>
            <div className="shikumi-price-note"><p><strong>診断＋初期整備：合計275,000円〜</strong></p><p>クラウドやAI APIなどの利用料は別途かかります。</p></div>
          </div>
        </section>

        <section className="shikumi-section shikumi-tinted" aria-labelledby="shikumi-process-title">
          <div className="shikumi-wrap shikumi-split">
            <div><p className="shikumi-eyebrow">ご相談の流れ</p><h2 id="shikumi-process-title">困っていることを<br />聞かせてください。</h2><p className="shikumi-section-lead">初回から詳しい技術説明は必要ありません。<br />今の使い方や、変えたいことから伺います。</p></div>
            <ol className="shikumi-process">
              <li><span>01</span><div><h3>無料相談</h3><p>用途や困りごと、ご希望を確認します。コードや仕様書は不要です。</p></div></li>
              <li><span>02</span><div><h3>有料診断</h3><p>ご依頼後にコードや構成を調査し、必要な改修をご提案します。</p></div></li>
              <li><span>03</span><div><h3>改修・運用の整備</h3><p>作業の範囲と料金に合意したうえで着手します。継続運用は任意です。</p></div></li>
            </ol>
          </div>
        </section>

        <section className="shikumi-section" aria-labelledby="shikumi-faq-title">
          <div className="shikumi-wrap shikumi-split">
            <div><p className="shikumi-eyebrow">よくあるご質問</p><h2 id="shikumi-faq-title">ご相談の前に。</h2></div>
            <div className="shikumi-faq">
              <details open><summary>仕様書がなくても相談できますか？</summary><p>はい。初回の無料相談にコードや仕様書は必要ありません。コードの詳しい調査は、有料診断で行います。</p></details>
              <details><summary>診断だけの依頼もできますか？</summary><p>はい。診断は55,000円（税込）の単発でご依頼いただけます。診断料金に実装・移行は含みません。</p></details>
              <details><summary>継続運用の契約は必須ですか？</summary><p>必須ではありません。必要に応じてお選びいただけます。月額33,000円（税込）で月3時間まで対応し、超過する作業は事前にお見積りします。</p></details>
            </div>
          </div>
        </section>

        <section className="shikumi-cta" aria-labelledby="shikumi-cta-title">
          <div className="shikumi-wrap">
            <p className="shikumi-eyebrow">つくったあと</p><h2 id="shikumi-cta-title">今あるツールのこと、<br />まずは気軽にご相談ください。</h2>
            <p>使い方や困りごとを伺い、次に確認することを整理します。</p>
            <div className="shikumi-actions"><a className="shikumi-button shikumi-button-light" href={contactUrl}>無料で相談する <span aria-hidden="true">↗</span></a><a className="shikumi-text-link" href={serviceUrl}>サービス公式サイト <span aria-hidden="true">↗</span></a></div>
            <p className="shikumi-note">サービス専用サイトのお問い合わせページへ移動します。</p>
          </div>
        </section>
      </main>
    </div>
  );
}
